import { useState, type FormEvent } from "react";

import { useAuth } from "@/auth/useAuth";
import { PageHeader } from "@/components/layout/PageHeader";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/lib/api/client";
import {
  OTP_LENGTH,
  OTP_RESEND_SECONDS,
  resetPasswordSchema,
  zodFieldErrors,
} from "@/lib/auth-validations";
import { useCountdown } from "@/lib/use-countdown";
import { requestPasswordReset, verifyPasswordReset } from "@/services/auth";

export function AccountPage() {
  const { session } = useAuth();
  const toast = useToast();
  const email = session?.profile.email ?? "";

  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const { seconds, start } = useCountdown(0);

  const sendCode = async () => {
    setError(null);
    setInfo(null);
    setSending(true);
    try {
      const message = await requestPasswordReset({ email });
      setInfo(message);
      setOtpSent(true);
      start(OTP_RESEND_SECONDS);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries(err.fieldErrors)) {
          next[key] = Array.isArray(value) ? value[0] : value;
        }
        setFieldErrors(next);
      } else {
        setError("Unable to send the reset code");
      }
    } finally {
      setSending(false);
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setInfo(null);

    const parsed = resetPasswordSchema.safeParse({ code, password, confirmPassword });
    if (!parsed.success) {
      setFieldErrors(zodFieldErrors(parsed.error));
      return;
    }

    setFieldErrors({});
    setSaving(true);
    try {
      await verifyPasswordReset({
        email,
        code: parsed.data.code,
        password: parsed.data.password,
      });
      toast.success("Password updated.");
      setOtpSent(false);
      setCode("");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries(err.fieldErrors)) {
          next[key] = Array.isArray(value) ? value[0] : value;
        }
        setFieldErrors(next);
      } else {
        setError("Unable to update password");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Account"
        title="Change password"
        description="We’ll email a 4-digit code to verify it’s you, then you can set a new password."
      />

      <form
        onSubmit={onSubmit}
        className="max-w-xl space-y-5 rounded-2xl border border-line bg-surface-raised p-5 sm:p-6"
        noValidate
        autoComplete="off"
      >
        {error ? <Alert>{error}</Alert> : null}
        {info ? <Alert tone="success">{info}</Alert> : null}

        <Input
          label="Email"
          name="email"
          type="email"
          value={email}
          readOnly
          hint="The code is sent to this address."
        />

        {otpSent ? (
          <>
            <Input
              label="Email OTP"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={OTP_LENGTH}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH))}
              error={fieldErrors.code}
              placeholder={"0".repeat(OTP_LENGTH)}
            />
            {seconds > 0 ? (
              <p className="text-sm text-muted">Resend code in {seconds}s</p>
            ) : (
              <button
                type="button"
                className="text-sm font-medium text-brand hover:text-brand-strong disabled:opacity-50"
                onClick={() => void sendCode()}
                disabled={sending}
              >
                {sending ? "Sending…" : "Resend OTP"}
              </button>
            )}
            <Input
              label="New password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={fieldErrors.password}
              hint="At least 6 characters, with upper, lower, number, and special character."
            />
            <Input
              label="Confirm password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={fieldErrors.confirmPassword}
            />
            <div className="flex items-center justify-end gap-2 border-t border-line pt-5">
              <Button type="submit" loading={saving}>
                Update password
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-end border-t border-line pt-5">
            <Button type="button" loading={sending} onClick={() => void sendCode()}>
              Send code
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
